import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Lock, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [checking, setChecking] = useState(true);
    const [invalid, setInvalid] = useState("");
    const [emailMasked, setEmailMasked] = useState("");
    const [pw1, setPw1] = useState("");
    const [pw2, setPw2] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/auth/reset-password/${token}`);
                setEmailMasked(data.email_masked || "");
            } catch (err) {
                setInvalid(err?.response?.data?.detail || "Invalid or expired reset link");
            } finally {
                setChecking(false);
            }
        })();
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (pw1.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        if (pw1 !== pw2) { toast.error("Passwords do not match"); return; }
        setSaving(true);
        try {
            await api.post(`/auth/reset-password/${token}`, { new_password: pw1 });
            toast.success("Password updated. Please sign in.");
            navigate("/admin/login");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not reset password");
        } finally {
            setSaving(false);
        }
    };

    if (checking) {
        return <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="reset-loading">Validating link…</div>;
    }
    if (invalid) {
        return (
            <div className="min-h-screen grid place-items-center noise-bg px-6" data-testid="reset-invalid">
                <div className="max-w-md rounded-2xl border bg-white p-8 text-center">
                    <h1 className="rotary-heading text-2xl text-rotary-ink">Link invalid</h1>
                    <p className="mt-2 text-sm text-muted-foreground">{invalid}</p>
                    <Link to="/admin/forgot-password" className="mt-4 inline-block text-sm text-rotary-blue hover:underline">Request a new link</Link>
                </div>
            </div>
        );
    }
    return (
        <div className="min-h-screen grid place-items-center noise-bg px-6" data-testid="reset-password-page">
            <form onSubmit={submit} className="w-full max-w-md rounded-2xl border bg-white p-8 shadow-sm animate-fade-up">
                <div className="mb-6 flex items-center gap-3">
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-rotary-blue text-white">
                        <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div>
                        <span className="label-eyebrow">Set a new password</span>
                        <h1 className="rotary-heading text-2xl text-rotary-ink leading-tight">Reset password</h1>
                    </div>
                </div>
                <p className="text-sm text-muted-foreground mb-5">
                    Setting a new password for <span className="font-medium text-rotary-ink" data-testid="reset-email-masked">{emailMasked}</span>
                </p>
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="pw1" className="label-eyebrow">New password</Label>
                        <div className="relative mt-2">
                            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input id="pw1" type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} className="pl-9 h-11" data-testid="reset-pw1" required minLength={8} />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="pw2" className="label-eyebrow">Confirm password</Label>
                        <div className="relative mt-2">
                            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="pl-9 h-11" data-testid="reset-pw2" required minLength={8} />
                        </div>
                    </div>
                    <Button type="submit" disabled={saving} className="w-full h-11 rounded-full bg-rotary-blue text-white hover:bg-rotary-sky" data-testid="reset-submit-btn">
                        {saving ? "Saving…" : "Set new password"}
                    </Button>
                </div>
                <Link to="/admin/login" className="mt-6 inline-flex items-center gap-1 text-sm text-rotary-blue hover:underline" data-testid="reset-back-to-login">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                </Link>
            </form>
        </div>
    );
}
